import { useRef, useState, useCallback, useEffect } from "react";
import { Controller } from "react-hook-form";
import { Text, Group, Stack, Paper, Box, Modal } from "@mantine/core";
import { IconCamera, IconVideo, IconX, IconPhoto, IconMovie } from "@tabler/icons-react";
import { Button, Input } from "@onengine/component-library";
import type { Control } from "react-hook-form";
import type { Icon } from "@tabler/icons-react";
import { useHasMediaUrlResolver, useResolvedMediaUrl } from "./MediaUrlResolver";
import styles from "./MediaCaptureField.module.scss";

export type MediaCaptureType = "photo" | "video";

// Saved answers come back from web or mobile with only the stored `key`, so `file` and
// `previewUrl` exist just for media captured since the form was opened.
export interface CapturedMedia {
  slotId: string;
  file?: File;
  previewUrl?: string;
  description: string;
  key?: string;
  fileName?: string;
  fileSize?: number;
  contentType?: string;
}

export interface MediaSlot {
  id: string;
  label: string;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} b`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kb`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} mb`;
}

function SavedThumbnail({
  mediaKey,
  isPhoto,
  FileIcon,
}: {
  mediaKey: string;
  isPhoto: boolean;
  FileIcon: Icon;
}) {
  const url = useResolvedMediaUrl(mediaKey);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const label = isPhoto ? "Open photo" : "Open video";

  if (!url || failedUrl === url) {
    return (
      <div className={styles.thumbnail}>
        <FileIcon size={18} className={styles.fileIcon} />
      </div>
    );
  }

  return (
    <div className={styles.thumbnail}>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        className={styles.thumbnailLink}
      >
        {isPhoto ? (
          <img
            src={url}
            alt=""
            className={styles.previewImage}
            draggable={false}
            onError={() => setFailedUrl(url)}
          />
        ) : (
          <video
            src={url}
            className={styles.previewVideo}
            muted
            playsInline
            preload="metadata"
            aria-hidden
            onError={() => setFailedUrl(url)}
          />
        )}
      </a>
    </div>
  );
}

function CameraModal({
  opened,
  onClose,
  onCapture,
  mediaType,
}: {
  opened: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
  mediaType: MediaCaptureType;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const stopStream = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    recorderRef.current = null;
    chunksRef.current = [];
    setIsRecording(false);

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    try {
      const constraints: MediaStreamConstraints = {
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: mediaType === "video",
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch {
      setCameraError("Camera access denied or unavailable. Please grant camera permission.");
    }
  }, [mediaType]);

  useEffect(() => {
    if (opened) {
      startCamera();
    }
    return () => stopStream();
  }, [opened, startCamera, stopStream]);

  const handleClose = () => {
    stopStream();
    onClose();
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `photo-${Date.now()}.jpg`, { type: "image/jpeg" });
        stopStream();
        onCapture(file);
      },
      "image/jpeg",
      0.92,
    );
  };

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const recorder = new MediaRecorder(streamRef.current, { mimeType: "video/webm" });
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      const file = new File([blob], `video-${Date.now()}.webm`, { type: "video/webm" });
      stopStream();
      onCapture(file);
    };
    recorder.start();
    recorderRef.current = recorder;
    setIsRecording(true);
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={mediaType === "photo" ? "Take Photo" : "Record Video"}
      size="lg"
      centered
      radius="lg"
    >
      <Stack gap="md" align="center">
        {cameraError ? (
          <Text c="var(--text-danger-default)" size="sm" ta="center">
            {cameraError}
          </Text>
        ) : (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={mediaType === "photo"}
            style={{
              width: "100%",
              maxHeight: 400,
              borderRadius: "var(--radius-base)",
              background: "#000",
              objectFit: "cover",
            }}
          />
        )}

        {!cameraError && (
          <Group gap="sm">
            {mediaType === "photo" ? (
              <Button
                variant="primary"
                size="small"
                iconLeft={<IconCamera size={16} />}
                onClick={capturePhoto}
              >
                Capture Photo
              </Button>
            ) : isRecording ? (
              <Button variant="destructive-primary" size="small" onClick={stopRecording}>
                Stop Recording
              </Button>
            ) : (
              <Button
                variant="primary"
                size="small"
                iconLeft={<IconVideo size={16} />}
                onClick={startRecording}
              >
                Start Recording
              </Button>
            )}
          </Group>
        )}
      </Stack>
    </Modal>
  );
}

export interface MediaCaptureFieldProps {
  value: CapturedMedia[];
  onChange: (v: CapturedMedia[]) => void;
  slots: MediaSlot[];
  allowAdditional?: boolean;
  mediaType: MediaCaptureType;
  error?: string;
}

export function MediaCaptureField({
  value,
  onChange,
  slots,
  allowAdditional,
  mediaType,
  error,
}: MediaCaptureFieldProps) {
  const [cameraOpen, setCameraOpen] = useState(false);
  const [activeSlotId, setActiveSlotId] = useState<string | null>(null);
  const [nextAdditionalId, setNextAdditionalId] = useState(1);

  const isPhoto = mediaType === "photo";
  const label = isPhoto ? "Photo" : "Video";
  const SlotIcon = isPhoto ? IconCamera : IconVideo;
  const FileIcon = isPhoto ? IconPhoto : IconMovie;
  const hasResolver = useHasMediaUrlResolver();

  const openCameraForSlot = useCallback((slotId: string) => {
    setActiveSlotId(slotId);
    setCameraOpen(true);
  }, []);

  const openCameraForAdditional = useCallback(() => {
    const id = `additional-${nextAdditionalId}`;
    setActiveSlotId(id);
    setNextAdditionalId((n) => n + 1);
    setCameraOpen(true);
  }, [nextAdditionalId]);

  const handleCapture = useCallback(
    (file: File) => {
      if (!activeSlotId) return;
      const prev = value.find((m) => m.slotId === activeSlotId);
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
      const previewUrl = URL.createObjectURL(file);
      const existing = value.filter((m) => m.slotId !== activeSlotId);
      onChange([
        ...existing,
        { slotId: activeSlotId, file, previewUrl, description: prev?.description ?? "" },
      ]);
      setCameraOpen(false);
      setActiveSlotId(null);
    },
    [value, onChange, activeSlotId],
  );

  const handleRemove = useCallback(
    (slotId: string) => {
      const item = value.find((m) => m.slotId === slotId);
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      onChange(value.filter((m) => m.slotId !== slotId));
    },
    [value, onChange],
  );

  const handleDescriptionChange = useCallback(
    (slotId: string, desc: string) => {
      onChange(value.map((m) => (m.slotId === slotId ? { ...m, description: desc } : m)));
    },
    [value, onChange],
  );

  const additionalItems = value.filter((m) => m.slotId.startsWith("additional-"));

  const renderThumbnail = (item: CapturedMedia) => {
    if (isPhoto && item.previewUrl) {
      return (
        <Box className={styles.thumbnail}>
          <img
            key={item.previewUrl}
            src={item.previewUrl}
            alt=""
            className={styles.previewImage}
            draggable={false}
          />
        </Box>
      );
    }
    if (!isPhoto && item.previewUrl) {
      return (
        <Box className={styles.thumbnail}>
          <video
            key={item.previewUrl}
            src={item.previewUrl}
            className={styles.previewVideo}
            muted
            playsInline
            preload="metadata"
            aria-hidden
          />
        </Box>
      );
    }
    if (hasResolver && item.key) {
      return <SavedThumbnail mediaKey={item.key} isPhoto={isPhoto} FileIcon={FileIcon} />;
    }
    return (
      <Box className={styles.thumbnail}>
        <FileIcon size={18} className={styles.fileIcon} />
      </Box>
    );
  };

  const renderCapturedItem = (item: CapturedMedia) => (
    <Stack className={styles.capturedStack}>
      <Paper p="xs" radius="md" className={styles.capturedPaper}>
        <Group className={styles.row}>
          <Group className={styles.rowStart}>
            {renderThumbnail(item)}
            <Stack gap={0}>
              <Text size="sm" className={styles.fileName} lineClamp={1}>
                {item.file?.name ?? item.fileName ?? ""}
              </Text>
              <Text size="xs" className={styles.fileSize}>
                {formatFileSize(item.file?.size ?? item.fileSize ?? 0)}
              </Text>
            </Stack>
          </Group>
          <Button
            type="icon"
            variant="tertiary"
            size="small"
            iconLeft={<IconX size={16} />}
            onClick={() => handleRemove(item.slotId)}
          />
        </Group>
      </Paper>
      <Input
        value={item.description}
        onChange={(v) => handleDescriptionChange(item.slotId, v)}
        placeholder={`${label} Description`}
        size="small"
      />
    </Stack>
  );

  return (
    <Stack className={styles.rootStack}>
      {slots.map((slot, i) => {
        const captured = value.find((m) => m.slotId === slot.id);
        return (
          <Box key={slot.id}>
            {captured ? (
              renderCapturedItem(captured)
            ) : (
              <Paper
                p="xs"
                radius="md"
                className={styles.slotPaperClickable}
                onClick={() => openCameraForSlot(slot.id)}
              >
                <Group className={styles.row}>
                  <Text size="sm" className={styles.slotLabel}>
                    {slot.label || `${label} ${i + 1}`}
                  </Text>
                  <SlotIcon size={16} className={styles.slotTriggerIcon} />
                </Group>
              </Paper>
            )}
          </Box>
        );
      })}

      {additionalItems.map((item) => (
        <Box key={item.slotId}>{renderCapturedItem(item)}</Box>
      ))}

      {allowAdditional && (
        <Button
          variant="secondary"
          size="small"
          iconLeft={<SlotIcon size={16} />}
          className={styles.additionalButton}
          onClick={openCameraForAdditional}
        >
          {`Add additional ${label.toLowerCase()}s`}
        </Button>
      )}

      {error && (
        <Text size="xs" className={styles.error}>
          {error}
        </Text>
      )}

      <CameraModal
        opened={cameraOpen}
        onClose={() => {
          setCameraOpen(false);
          setActiveSlotId(null);
        }}
        onCapture={handleCapture}
        mediaType={mediaType}
      />
    </Stack>
  );
}

export interface ControlledMediaCaptureProps {
  name: string;
  control: Control<any>;
  error?: string;
  clearErrors: (name?: string) => void;
  slots: MediaSlot[];
  allowAdditional?: boolean;
  mediaType: MediaCaptureType;
}

export function ControlledMediaCapture({
  name,
  control,
  error,
  clearErrors,
  slots,
  allowAdditional,
  mediaType,
}: ControlledMediaCaptureProps) {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={[]}
      render={({ field }) => (
        <MediaCaptureField
          value={Array.isArray(field.value) ? field.value : []}
          onChange={(v) => {
            field.onChange(v);
            clearErrors(name);
          }}
          slots={slots}
          allowAdditional={allowAdditional}
          mediaType={mediaType}
          error={error}
        />
      )}
    />
  );
}
